import { useAsync } from 'react-async-hook';
import {
  Accessory,
  AccessoryConfig,
  SelectedAccessory,
} from '@upstart/harmony/lib/legos/accessoryTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { Dealer, DealerId } from '@upstart/harmony/lib/legos/dealerTypes';
import {
  isAccessoryApplicable,
  getAccessoryPrice,
} from '@upstart/harmony/lib/accessory-utils';
import AccessoryConfigService from '../../services/AccessoryConfigService';

interface Props {
  car?: Car;
  dealer?: Dealer;
  selected?: SelectedAccessory[];
}

const transformAccessory = (accessory: Accessory): SelectedAccessory => ({
  id: accessory.id,
  name: accessory.name ?? '',
  taxed: accessory.taxed ?? false,
  price: getAccessoryPrice(accessory),
});

async function getAccessoryConfig(dealerId: DealerId) {
  return dealerId
    ? AccessoryConfigService.memoizedGetAccessoryConfig(dealerId)
    : null;
}

const useAccessories = (props: Props) => {
  const carDealerId = props.car?.dealer_id || props.dealer?.id;
  const asyncAccessoryConfig = useAsync<AccessoryConfig>(getAccessoryConfig, [
    carDealerId,
  ]);

  const toggleAccessory = (accessory: Accessory) => {
    const selected = [...(props.selected ?? [])];
    if (selected.some(a => a.id === accessory.id)) {
      return selected.filter(s => s.id !== accessory.id);
    }
    return selected.concat(transformAccessory(accessory));
  };

  // Get applicable accessories
  const accessories = (asyncAccessoryConfig.result?.accessories ?? [])
    .filter(a => isAccessoryApplicable(a, props.car))
    .concat(props.car?.optionalAccessories ?? []);

  // Calculate total price
  const totalAccessoryPrice = accessories
    .filter(a => props.selected?.some(s => s.id === a.id))
    .reduce((total, a) => total + getAccessoryPrice(a), 0);

  return {
    fetching: asyncAccessoryConfig.loading,
    accessoryConfig: asyncAccessoryConfig.result,
    accessories,
    toggleAccessory,
    totalAccessoryPrice,
  };
};

export default useAccessories;
